import React, { useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Box, Container, List, Stack, Alert } from '@mantine/core'
import { AppButton, AppText } from '../../app/common/ui'
import { HELP_CONTENT, isAmmoHelpFieldKey } from './ammoHelpContent'
import { RECIPE_HELP_CONTENT, isRecipeHelpFieldKey } from './recipeHelpContent'
import {
  AMMO_SUBMIT_RETURN_PATH,
  AMMO_SUBMIT_RETURN_TITLE,
  decodeAmmoSubmitStateParam,
  saveAmmoSubmitFormState
} from './ammoSubmitFormState'
import {
  decodeRecipeSubmitStateParam,
  isRecipeSubmitReturnPath,
  saveRecipeSubmitFormState
} from './recipeSubmitFormState'

function parseReturnTo (search: string): string {
  const params = new URLSearchParams(search)
  const raw = params.get('returnTo')?.trim()
  if (!raw) return AMMO_SUBMIT_RETURN_PATH
  if (!raw.startsWith('/') || raw.startsWith('//')) return AMMO_SUBMIT_RETURN_PATH
  return raw
}

function parsePageTitle (returnTo: string): string {
  if (returnTo === '/ammo/submit') return 'Ammo Submit'
  if (returnTo.startsWith('/ammo/reloading')) return 'Recipe Submit'
  return AMMO_SUBMIT_RETURN_TITLE
}

export default function AmmoHelpPage () {
  const { field = '' } = useParams<{ field: string }>()
  const location = useLocation()
  const history = useHistory()

  const returnTo = useMemo(() => parseReturnTo(location.search), [location.search])
  const pageTitle = useMemo(() => parsePageTitle(returnTo), [returnTo])

  const content = isRecipeHelpFieldKey(field)
    ? RECIPE_HELP_CONTENT[field]
    : isAmmoHelpFieldKey(field)
      ? HELP_CONTENT[field]
      : null

  // If help was opened with state in the URL, mirror it into sessionStorage for return navigation.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search)
    const stateParam = params.get('state')
    if (!stateParam) return

    if (isRecipeSubmitReturnPath(returnTo)) {
      const fromUrl = decodeRecipeSubmitStateParam(stateParam)
      if (fromUrl) {
        saveRecipeSubmitFormState({ form: fromUrl.form })
      }
      return
    }

    const fromUrl = decodeAmmoSubmitStateParam(stateParam)
    if (fromUrl) {
      saveAmmoSubmitFormState({
        activeStep: fromUrl.activeStep,
        activeTab: fromUrl.activeTab,
        specForm: fromUrl.specForm,
        ammoForm: fromUrl.ammoForm,
        matchedCartridgeSpecId: fromUrl.matchedCartridgeSpecId
      })
    }
  }, [location.search, returnTo])

  const handleBack = () => {
    history.push(returnTo)
  }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh', paddingTop: '24px', paddingBottom: '32px' }}>
      <Container size="sm">
        <Stack spacing="lg">
          <AppButton
            variant="subtle"
            onClick={handleBack}
            data-testid="help-breadcrumb-back"
            sx={{ width: 'fit-content', paddingLeft: 0 }}
          >
            ← Back to {pageTitle}
          </AppButton>

          {!content ? (
            <Alert color="red" title="Help topic not found">
              No help article exists for &quot;{field}&quot;.
            </Alert>
          ) : (
            <>
              <Box>
                <AppText sx={{ fontSize: '26px', fontWeight: 700, marginBottom: '8px' }}>
                  {content.title}
                </AppText>
                <AppText sx={{ color: 'var(--gs-muted)', lineHeight: 1.6 }}>
                  {content.definition}
                </AppText>
              </Box>

              <Box>
                <AppText weight={600} sx={{ marginBottom: '8px' }}>
                  Examples
                </AppText>
                <List size="sm" spacing="xs">
                  {content.examples.map((example) => (
                    <List.Item key={example}>{example}</List.Item>
                  ))}
                </List>
              </Box>

              <Alert color="yellow" title="Please note">
                {content.safetyNote}
              </Alert>
            </>
          )}

          <AppButton variant="light" onClick={handleBack} data-testid="help-back-button">
            Return to form
          </AppButton>
        </Stack>
      </Container>
    </Box>
  )
}
